/**
 * Model kalendarza planowania meczow na kortach klubu.
 *
 * Czyste funkcje bez Reacta: pozycjonowanie slotow na osi czasu, przyciaganie
 * upuszczen do siatki i lokalne wykrywanie kolizji zanim poleci zapis (backend
 * i tak waliduje to samo i odpowiada 409 z opisem).
 */

import type { ApiClubCourt, ApiCourtBlock, ApiPlannerMatch } from './courtTypes';

/** Pierwsza godzina osi kalendarza. */
export const PLANNER_START_HOUR = 6;
/** Ostatnia godzina osi (wlacznie jako etykieta konca). */
export const PLANNER_END_HOUR = 23;
/** Krok przyciagania upuszczanego meczu, w minutach. */
export const SLOT_STEP_MINUTES = 15;
/**
 * Prog magnesu do krawedzi sasiednich slotow, w minutach. Kursor blizej niz
 * prog od styku z innym meczem/przerwa laduje dokladnie na styku — bez tego
 * ustawienie meczu „od razu po poprzednim" wymagaloby celowania w okno
 * +-7,5 minuty siatki.
 */
export const MAGNET_THRESHOLD_MINUTES = 10;

/** Zamienia `HH:mm:ss` albo `HH:mm` na liczbe minut od polnocy. */
export function toMinutes(time: string): number {
  const [hours = '0', minutes = '0'] = time.split(':');
  return Number(hours) * 60 + Number(minutes);
}

/** Skraca `HH:mm:ss` do `HH:mm`; puste wejscie zwraca pusty napis. */
export function hhmm(time: string | null | undefined): string {
  return time ? time.slice(0, 5) : '';
}

/** Zamienia minuty od polnocy na `HH:mm`. */
export function fromMinutes(minutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60 - 1, Math.round(minutes)));
  const h = String(Math.floor(clamped / 60)).padStart(2, '0');
  const m = String(clamped % 60).padStart(2, '0');
  return `${h}:${m}`;
}

/** Godziny naglowka osi kalendarza, np. 6 … 23. */
export function plannerHours(): number[] {
  const hours: number[] = [];
  for (let hour = PLANNER_START_HOUR; hour <= PLANNER_END_HOUR; hour += 1) {
    hours.push(hour);
  }
  return hours;
}

/** Dlugosc osi kalendarza w minutach. */
export function plannerAxisMinutes(): number {
  return (PLANNER_END_HOUR - PLANNER_START_HOUR) * 60;
}

/**
 * Przyciaga pozycje upuszczenia (w pikselach osi) do godziny startu `HH:mm`.
 *
 * Najpierw probuje magnesow (`magnets` — kandydackie starty „na styk"
 * z {@link magnetStarts}): kursor blizej niz {@link MAGNET_THRESHOLD_MINUTES}
 * od magnesu laduje dokladnie na nim. Poza zasiegiem magnesow obowiazuje
 * siatka co {@link SLOT_STEP_MINUTES}. Wynik nie wychodzi poza os.
 */
export function snapDropToTime(
  offsetPx: number,
  axisHeightPx: number,
  magnets: readonly number[] = [],
): string {
  const axisStart = PLANNER_START_HOUR * 60;
  const axisSpan = plannerAxisMinutes();
  const raw = axisStart + (offsetPx / Math.max(1, axisHeightPx)) * axisSpan;

  // Magnes ma pierwszenstwo przed siatka — „mniej wiecej na styk" znaczy styk.
  let nearest: number | undefined;
  for (const magnet of magnets) {
    if (Math.abs(raw - magnet) > MAGNET_THRESHOLD_MINUTES) continue;
    if (nearest === undefined || Math.abs(raw - magnet) < Math.abs(raw - nearest)) {
      nearest = magnet;
    }
  }
  if (nearest !== undefined) {
    return fromMinutes(Math.max(axisStart, Math.min(axisStart + axisSpan, nearest)));
  }

  const snapped = Math.round(raw / SLOT_STEP_MINUTES) * SLOT_STEP_MINUTES;
  const clamped = Math.max(axisStart, Math.min(axisStart + axisSpan - SLOT_STEP_MINUTES, snapped));
  return fromMinutes(clamped);
}

/**
 * Kandydackie starty „na styk" dla przeciaganego meczu na wskazanym korcie:
 * koniec kazdego innego meczu i przerwy technicznej (mecz zaraz PO) oraz ich
 * poczatek minus czas trwania (mecz konczacy sie rowno PRZED nimi). Wartosci
 * w minutach od polnocy; kandydaci wystajacy poza os sa pomijani.
 */
export function magnetStarts(
  scheduled: readonly ApiPlannerMatch[],
  court: ApiClubCourt,
  date: string,
  moved: MovedMatch,
): number[] {
  const axisStart = PLANNER_START_HOUR * 60;
  const axisEnd = PLANNER_END_HOUR * 60;
  const starts: number[] = [];

  function push(slotStart: number, slotEnd: number) {
    // „Zaraz po" — start w koncu sasiada; caly mecz musi zmiescic sie na osi.
    if (slotEnd >= axisStart && slotEnd + moved.durationMinutes <= axisEnd) {
      starts.push(slotEnd);
    }
    // „Rowno przed" — koniec przeciaganego meczu w poczatku sasiada.
    const before = slotStart - moved.durationMinutes;
    if (before >= axisStart && slotStart <= axisEnd) {
      starts.push(before);
    }
  }

  for (const entry of scheduled) {
    const match = entry.match;
    if (match.id === moved.matchId) continue;
    if (match.courtId !== court.id || match.scheduledDate !== date || !match.scheduledTime) {
      continue;
    }
    const start = toMinutes(match.scheduledTime);
    push(start, start + entry.durationMinutes);
  }
  for (const block of blocksOnDay(court, date)) {
    push(toMinutes(block.startTime), toMinutes(block.endTime));
  }
  return starts;
}

/** Pasek na osi czasu wyrazony w procentach jej dlugosci. */
export interface SlotBar {
  /** Poczatek paska liczony od poczatku osi, w procentach. */
  startPercent: number;
  /** Dlugosc paska, w procentach osi. */
  sizePercent: number;
}

/**
 * Przelicza slot (start + czas trwania) na pozycje paska. Zakresy wystajace
 * poza os sa przycinane do jej krawedzi, zeby nie rozjezdzac siatki.
 */
export function slotBar(startTime: string, durationMinutes: number): SlotBar {
  const axisStart = PLANNER_START_HOUR * 60;
  const axisEnd = PLANNER_END_HOUR * 60;
  const axisSpan = axisEnd - axisStart;

  const start = Math.max(axisStart, Math.min(axisEnd, toMinutes(startTime)));
  const end = Math.max(axisStart, Math.min(axisEnd, toMinutes(startTime) + durationMinutes));

  return {
    startPercent: ((start - axisStart) / axisSpan) * 100,
    sizePercent: Math.max(0, ((end - start) / axisSpan) * 100),
  };
}

/**
 * Uklad karty meczu na kalendarzu, dobierany do wysokosci slotu.
 *
 * Wysokosc karty jest proporcjonalna do czasu trwania meczu, wiec krotki mecz
 * dostaje niski prostokat i pelny uklad przestaje sie w nim miescic — nazwiska
 * zostaja przyciete w polowie liter.
 *
 * Obie strony meczu ZAWSZE stoja jedna pod druga, niezaleznie od gestosci:
 * w deblu jedna linia to cala para, wiec sklejenie stron daloby nieczytelny
 * ciag czterech nazwisk. Kurczy sie wylacznie czcionka i wiersz z godzina.
 */
export type MatchCardDensity = 'full' | 'compact' | 'tight';

/** Wysokosc slotu w rem — czas trwania przelozony na siatke kalendarza. */
export function slotHeightRem(durationMinutes: number, hourHeightRem: number): number {
  return (durationMinutes / 60) * hourHeightRem;
}

/**
 * Dobiera uklad karty do wysokosci slotu.
 *
 * Progi wynikaja z wysokosci samej tresci:
 * - `full` — wiersz godzin (0.875rem, tyle ma ikona) + dwie linie po 0.9375rem
 *   + wypelnienie 0.5rem, razem ~3.25rem;
 * - `compact` — godzina wlaczona w pierwsza linie nazwisk, dwie linie po
 *   0.78rem + wypelnienie 0.25rem, razem ~1.8rem (prog z zapasem: 2rem);
 * - `tight` — dwie linie po 0.625rem bez wypelnienia, razem ~1.25rem.
 */
export function matchCardDensity(heightRem: number): MatchCardDensity {
  if (heightRem >= 3.25) return 'full';
  if (heightRem >= 2) return 'compact';
  return 'tight';
}

/** Czy dwa zakresy minutowe nachodza na siebie. Styk godzin nie jest kolizja. */
export function overlapsMinutes(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): boolean {
  return aStart < bEnd && bStart < aEnd;
}

/** Opis kolizji wykrytej lokalnie przed wyslaniem zapisu. */
export interface SlotConflict {
  /**
   * `match` — kort zajety innym meczem, `block` — przerwa techniczna,
   * `player` — zawodnik gra o tej porze gdzie indziej (takze w innym turnieju).
   */
  kind: 'match' | 'block' | 'player';
  /** Etykieta zrodla kolizji: nazwa rozgrywki albo powod przerwy. */
  label: string | null;
  startTime: string;
  endTime: string;
  /** Zajety zawodnik — wypelniane wylacznie dla `kind: 'player'`. */
  playerId?: string;
}

/** Mecz przenoszony na kalendarzu — wszystko, co decyduje o kolizjach slotu. */
export interface MovedMatch {
  /** Dlugosc slotu w minutach. */
  durationMinutes: number;
  /** Identyfikator meczu — mecz nie koliduje sam ze soba. */
  matchId?: string;
  /** Zawodnicy obu stron meczu; pusty zbior wylacza sprawdzanie zawodnikow. */
  playerIds?: readonly string[];
}

/** Zawodnicy obu stron meczu — zrodlo `MovedMatch.playerIds`. */
export function matchPlayerIds(entry: ApiPlannerMatch): string[] {
  return [...entry.match.team1Ids, ...entry.match.team2Ids];
}

/**
 * Szuka kolizji proponowanego slotu: z meczami juz rozplanowanymi na korcie,
 * z przerwami technicznymi tego dnia i z innymi meczami zawodnikow.
 *
 * Kolizja zawodnika idzie na koncu celowo. Zajety kort organizator naprawia
 * przenosinami na inny kort, wiec ta informacja jest pilniejsza; dopiero gdy
 * kort jest wolny, ma sens komunikat „ten zawodnik gra teraz gdzie indziej".
 */
export function findSlotConflict(
  scheduled: readonly ApiPlannerMatch[],
  court: ApiClubCourt,
  date: string,
  startTime: string,
  moved: MovedMatch,
): SlotConflict | null {
  const start = toMinutes(startTime);
  const end = start + moved.durationMinutes;

  /** Czy wpis stoi tego dnia i nachodzi czasowo na proponowany slot. */
  function collidesInTime(entry: ApiPlannerMatch): boolean {
    const match = entry.match;
    if (match.id === moved.matchId) return false;
    if (match.scheduledDate !== date || !match.scheduledTime) return false;
    const otherStart = toMinutes(match.scheduledTime);
    return overlapsMinutes(start, end, otherStart, otherStart + entry.durationMinutes);
  }

  for (const entry of scheduled) {
    if (entry.match.courtId !== court.id || !collidesInTime(entry)) continue;
    const otherStart = toMinutes(entry.match.scheduledTime!);
    return {
      kind: 'match',
      label: entry.competitionName,
      startTime: fromMinutes(otherStart),
      endTime: fromMinutes(otherStart + entry.durationMinutes),
    };
  }

  for (const block of blocksOnDay(court, date)) {
    if (overlapsMinutes(start, end, toMinutes(block.startTime), toMinutes(block.endTime))) {
      return {
        kind: 'block',
        label: block.reason,
        startTime: hhmm(block.startTime),
        endTime: hhmm(block.endTime),
      };
    }
  }

  // Zawodnik gra tylko jeden mecz naraz — takze wtedy, gdy drugi nalezy do
  // innego turnieju (typowo: ten sam gracz w singlu i w deblu tego samego dnia).
  // Dlatego szukamy po WSZYSTKICH kortach, nie tylko po tym pod kursorem.
  const players = new Set(moved.playerIds ?? []);
  if (players.size > 0) {
    for (const entry of scheduled) {
      if (!collidesInTime(entry)) continue;
      const busy = matchPlayerIds(entry).find((id) => players.has(id));
      if (busy === undefined) continue;
      const otherStart = toMinutes(entry.match.scheduledTime!);
      return {
        kind: 'player',
        label: entry.competitionName,
        startTime: fromMinutes(otherStart),
        endTime: fromMinutes(otherStart + entry.durationMinutes),
        playerId: busy,
      };
    }
  }

  return null;
}

/** Przerwy techniczne kortu przypadajace na wskazany dzien, chronologicznie. */
export function blocksOnDay(court: ApiClubCourt, date: string): ApiCourtBlock[] {
  return court.blocks
    .filter((block) => block.date === date)
    .slice()
    .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));
}

/** Mecze rozplanowane na wskazanym korcie, chronologicznie. */
export function matchesOnCourt(
  scheduled: readonly ApiPlannerMatch[],
  courtId: string,
): ApiPlannerMatch[] {
  return scheduled
    .filter((entry) => entry.match.courtId === courtId)
    .slice()
    .sort(
      (a, b) => toMinutes(a.match.scheduledTime ?? '00:00') - toMinutes(b.match.scheduledTime ?? '00:00'),
    );
}

/**
 * Proponuje pierwszy wolny numer kortu w klubie.
 * Numery musza byc unikalne, a organizator zwykle doklada kolejny po istniejacych.
 */
export function nextCourtNumber(courts: readonly ApiClubCourt[]): number {
  const taken = new Set(courts.map((court) => court.number));
  let candidate = 1;
  while (taken.has(candidate)) candidate += 1;
  return candidate;
}

/** Pula meczow jednej rozgrywki — sekcja listy „do rozplanowania". */
export interface PoolSection {
  competitionId: string;
  competitionName: string;
  matches: ApiPlannerMatch[];
}

/**
 * Grupuje pule nierozplanowanych meczow po rozgrywkach, zachowujac kolejnosc
 * pierwszego wystapienia — mecze jednego turnieju maja stac razem.
 */
export function groupPoolByCompetition(pool: readonly ApiPlannerMatch[]): PoolSection[] {
  const sections = new Map<string, PoolSection>();
  for (const entry of pool) {
    const competitionId = entry.match.competitionId ?? 'unknown';
    const section = sections.get(competitionId) ?? {
      competitionId,
      competitionName: entry.competitionName ?? '',
      matches: [],
    };
    section.matches.push(entry);
    sections.set(competitionId, section);
  }
  return [...sections.values()];
}

/**
 * Stabilny indeks koloru rozgrywki — po nim kalendarz odroznia turnieje.
 * Kolejnosc listy `competitions` z plannera jest stala (sort backendu),
 * wiec indeks nie skacze miedzy odswiezeniami.
 */
export function competitionColorIndex(
  competitionIds: readonly string[],
  competitionId: string | null,
): number {
  if (!competitionId) return 0;
  const index = competitionIds.indexOf(competitionId);
  return index >= 0 ? index : 0;
}
